import {
  ChannelFieldRGBAData,
  COLOR_MODE,
  DataMessage,
  ImageAutoleveler,
  ImageColorer,
  ImageNoiseSuppressor,
  ImageScaler,
} from '@ouster/webviz';
import {
  ColorPaletteName,
  ImageSettings,
} from '@ouster/webviz-react-components';

import type { Observable, OperatorFunction } from 'rxjs';
import {
  catchError,
  filter,
  map,
  NEVER,
  share,
  Subject,
  takeUntil,
} from 'rxjs';

import { SDKSensorInfoAugmented } from '../types';
import {
  isReflectivityColorMode,
  isRgbColorMode,
  isWindowColorMode,
  shouldDisableAutoLevelsForMode,
} from './utils/vizColorModePolicyUtils';
import {
  normalizeSelectedSensorImageOptions,
  normalizeSelectedSensorImageSettingsForAvailableModes,
  normalizeVisibleImageSettingsForDisplay,
  resolveReturnIndexFromColorMode,
  setImageReturnsNoToggle,
  setRgbPaletteOption,
} from './utils/vizImageSettingsUtils';
import { findSensorMetadataByIdentifier } from './utils/vizSceneSensorSelectionUtils';
import { applyPerImageTransforms } from './vizSceneTwoDImageProcessing';
import { allColorPalettes, DEFAULT_IMAGE_PALETTE } from '../utils/constants';
import { copyDataMessage } from '../utils/misc';

// TODO(visualization): Remove image returnsSettings compatibility scaffolding
// once @ouster/webviz-react-components no longer requires returnsSettings in
// ImageSettings. Image return selection is now derived from image color mode.

export type ShowTwoDImagePanel = boolean;
export type ShowTwoDImageRulers = boolean;

export type TwoDImageState = {
  imageSettings: Record<string, ImageSettings[]>;
  showImagePanel: ShowTwoDImagePanel;
  showImageRulers: ShowTwoDImageRulers;
  imageInfo: {
    [sensorName: string]: {
      aspectRatio: number;
      dimensions: [number, number];
    };
  };
  imageObservables: Observable<ChannelFieldRGBAData>[];
  selectedImagePanelSn: string | null;
  // we need to clear the image observable when a different sensor is selected
  imageObservableDestroy$?: Subject<void>;
};

export const DEFAULT_IMAGE_COLOR_MODES = [
  COLOR_MODE.SIGNAL,
  COLOR_MODE.REFLECTIVITY,
];

// export const DEFAULT_IMAGE_COLOR_PALETTES = ['GREY'];
export const DEFAULT_IMAGE_COLOR_PALETTES = [DEFAULT_IMAGE_PALETTE];

export const defaultImageSettings: ImageSettings = {
  showImage: true,
  coloringMode: COLOR_MODE.SIGNAL,
  colorPalette: DEFAULT_IMAGE_PALETTE,
  colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
  colorModeOptions: [COLOR_MODE.SIGNAL, COLOR_MODE.REFLECTIVITY],
  isAutoLevelsDisabled: false,
  isAutoLevelsOn: true,
  isNoiseSuppressionDisabled: true,
  isNoiseSuppressionOn: false,
  returnsSettings: [{ visible: true, disabled: true }],
};

// TODO (mw) Will need to check data profile for this ultimately
export const defaultTwoDImageSettings: TwoDImageState = {
  imageSettings: {},
  showImagePanel: true,
  showImageRulers: true,
  imageInfo: {},
  imageObservables: [],
  selectedImagePanelSn: null,
};

export const selectSelectedImagePanelSn = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): string | null => state.twoDImageSettings.selectedImagePanelSn;

export const selectSelectedSensorImageSettings = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): ImageSettings[] => {
  const selectedSn = state.twoDImageSettings.selectedImagePanelSn;
  if (!selectedSn) return [];

  return state.twoDImageSettings.imageSettings[selectedSn] ?? [];
};

export const selectSelectedSensorPrimaryImageSetting = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): ImageSettings | undefined =>
  selectSelectedSensorImageSettings(state)[0] ?? undefined;

export const selectSelectedPrimaryImageContext = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): { sensorSn: string; imageSetting: ImageSettings } | undefined => {
  const sensorSn = state.twoDImageSettings.selectedImagePanelSn;
  if (!sensorSn) return undefined;

  const imageSetting = state.twoDImageSettings.imageSettings[sensorSn]?.[0];
  if (!imageSetting) return undefined;

  return { sensorSn, imageSetting };
};

export const selectEffectiveImageColorMode = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): COLOR_MODE | string | undefined =>
  selectSelectedSensorPrimaryImageSetting(state)?.coloringMode;

export const selectEffectiveImageColorPalette = (
  state: Pick<{ twoDImageSettings: TwoDImageState }, 'twoDImageSettings'>
): string | undefined =>
  selectSelectedSensorPrimaryImageSetting(state)?.colorPalette;

export const mergeTwoDImageSettingsState = ({
  currentState,
  partialNewSettings,
}: {
  currentState: TwoDImageState;
  partialNewSettings: Partial<TwoDImageState>;
}): TwoDImageState => {
  const mergedState = { ...currentState, ...partialNewSettings };

  if (partialNewSettings.imageSettings) {
    mergedState.imageSettings = {
      ...(currentState.imageSettings ?? {}),
      ...partialNewSettings.imageSettings,
    };
  }

  if (!mergedState.imageSettings) {
    mergedState.imageSettings = {};
  }

  return mergedState;
};

type ImageColorModeUpdatePayload = {
  sensorSn: string;
  index: number;
  partialSettings: Partial<ImageSettings>;
};

type ImagePaletteMemory = Record<
  string,
  Record<number, Record<string, ColorPaletteName>>
>;

export type ImageColorModeMemory = Record<
  string,
  Record<number, COLOR_MODE | string>
>;

type ImageAutoLevelsMemory = Record<string, Record<number, boolean>>;

type ImageColorState = {
  twoDImageSettings: TwoDImageState;
};

export const buildSetImageColorModeUpdatePayload = (
  state: ImageColorState,
  newImageColorMode: COLOR_MODE | string
): ImageColorModeUpdatePayload | undefined => {
  const selectedPrimaryContext = selectSelectedPrimaryImageContext(state);
  if (!selectedPrimaryContext) {
    return undefined;
  }

  return {
    sensorSn: selectedPrimaryContext.sensorSn,
    index: 0,
    partialSettings: { coloringMode: newImageColorMode },
  };
};

export const buildSetImageColorPaletteUpdatePayload = (
  state: ImageColorState,
  palette: ColorPaletteName
): ImageColorModeUpdatePayload | undefined => {
  const selectedPrimaryContext = selectSelectedPrimaryImageContext(state);
  if (!selectedPrimaryContext) {
    return undefined;
  }

  return {
    sensorSn: selectedPrimaryContext.sensorSn,
    index: 0,
    partialSettings: { colorPalette: palette },
  };
};

type InitialImageSelectionAssessment = {
  fallbackImageMode: string;
  hasCustomPalette: boolean;
  shouldPreserveExplicitSelection: boolean;
};

const assessInitialImageSelection = ({
  first,
  orderedValidImageModes,
  rgbChannels,
  hasExplicitColorModeSelection,
}: {
  first: ImageSettings;
  orderedValidImageModes: string[];
  rgbChannels: string[];
  hasExplicitColorModeSelection: boolean;
}): InitialImageSelectionAssessment => {
  const signalModeAvailable = orderedValidImageModes.includes(
    defaultImageSettings.coloringMode
  );

  const fallbackImageMode =
    orderedValidImageModes.find((mode) =>
      DEFAULT_IMAGE_COLOR_MODES.includes(mode as COLOR_MODE)
    ) ?? orderedValidImageModes[0];

  const isSystemFallbackColoringMode =
    !signalModeAvailable &&
    typeof first.coloringMode === 'string' &&
    first.coloringMode === fallbackImageMode;

  const expectedPaletteForFallback = isReflectivityColorMode(first.coloringMode)
    ? ('CalRef' as ColorPaletteName)
    : defaultImageSettings.colorPalette;

  const isAutoLevelsDisabledForFallback = shouldDisableAutoLevelsForMode({
    coloringMode: first.coloringMode,
    colorPalette: expectedPaletteForFallback,
    rgbChannels,
  });
  const expectedAutoLevelsOnForFallback = !isAutoLevelsDisabledForFallback;
  const expectedNoiseSuppressionOnForFallback =
    first.coloringMode === String(COLOR_MODE.NEAR_IR);

  const hasCustomColoringMode =
    typeof first.coloringMode === 'string' &&
    first.coloringMode !== defaultImageSettings.coloringMode &&
    signalModeAvailable;

  const hasCustomPalette =
    typeof first.colorPalette === 'string' &&
    (isSystemFallbackColoringMode
      ? first.colorPalette !== expectedPaletteForFallback
      : first.colorPalette !== defaultImageSettings.colorPalette);

  const hasCustomAutoLevels =
    typeof first.isAutoLevelsOn === 'boolean' &&
    (isSystemFallbackColoringMode
      ? first.isAutoLevelsOn !== expectedAutoLevelsOnForFallback
      : first.isAutoLevelsOn !== defaultImageSettings.isAutoLevelsOn);

  const hasCustomNoiseSuppression =
    typeof first.isNoiseSuppressionOn === 'boolean' &&
    (isSystemFallbackColoringMode
      ? first.isNoiseSuppressionOn !== expectedNoiseSuppressionOnForFallback
      : first.isNoiseSuppressionOn !==
        defaultImageSettings.isNoiseSuppressionOn);

  return {
    fallbackImageMode,
    hasCustomPalette,
    shouldPreserveExplicitSelection:
      hasExplicitColorModeSelection ||
      hasCustomColoringMode ||
      hasCustomPalette ||
      hasCustomAutoLevels ||
      hasCustomNoiseSuppression,
  };
};

export const initializeSelectedImageDefaultsForSensor = ({
  currentImageSettings,
  selectedImagePanelSn,
  orderedValidImageModes,
  rgbChannels,
  rgbAvailable,
  lastUserImageColorModeBySensorIdx,
}: {
  currentImageSettings: Record<string, ImageSettings[]>;
  selectedImagePanelSn: string;
  orderedValidImageModes: string[];
  rgbChannels: string[];
  rgbAvailable: boolean;
  lastUserImageColorModeBySensorIdx?: ImageColorModeMemory;
}): Record<string, ImageSettings[]> | undefined => {
  const sensorImageSettings = currentImageSettings[selectedImagePanelSn];
  if (!sensorImageSettings || sensorImageSettings.length === 0) {
    return undefined;
  }
  if (orderedValidImageModes.length === 0) {
    return undefined;
  }

  const first = sensorImageSettings[0];
  first.colorModeOptions = orderedValidImageModes;

  const sensorSupportedRgbModes = orderedValidImageModes.filter((mode) =>
    isRgbColorMode({
      coloringMode: mode,
      rgbChannels,
    })
  );
  const sensorSupportsRgb = sensorSupportedRgbModes.length > 0;

  const {
    fallbackImageMode,
    hasCustomPalette,
    shouldPreserveExplicitSelection,
  } = assessInitialImageSelection({
    first,
    orderedValidImageModes,
    rgbChannels,
    hasExplicitColorModeSelection:
      lastUserImageColorModeBySensorIdx?.[selectedImagePanelSn]?.[0] ===
      first.coloringMode,
  });

  if (sensorSupportsRgb && !shouldPreserveExplicitSelection) {
    if (rgbAvailable && sensorSupportedRgbModes.includes(COLOR_MODE.RGB)) {
      first.coloringMode = COLOR_MODE.RGB;
    } else {
      const [firstRgbChannel] = sensorSupportedRgbModes;
      first.coloringMode = firstRgbChannel;
    }

    setRgbPaletteOption(first, true);
    first.colorPalette = 'RGB';
    first.isAutoLevelsOn = true;
    first.isAutoLevelsDisabled = false;
    first.isNoiseSuppressionOn = false;
    first.isNoiseSuppressionDisabled = true;
  } else {
    setRgbPaletteOption(first, sensorSupportsRgb);

    const rgbModeInvalidForCurrentSensor =
      !sensorSupportsRgb &&
      isRgbColorMode({
        coloringMode: first.coloringMode,
        rgbChannels,
      });

    if (
      !orderedValidImageModes.includes(first.coloringMode) ||
      rgbModeInvalidForCurrentSensor
    ) {
      first.coloringMode = fallbackImageMode;
    }

    if (first.colorPalette === 'RGB') first.colorPalette = 'Greyscale';

    if (isReflectivityColorMode(first.coloringMode) && !hasCustomPalette) {
      first.colorPalette = 'CalRef';
    }

    const isAutoLevelsDisabled = shouldDisableAutoLevelsForMode({
      coloringMode: first.coloringMode,
      colorPalette: first.colorPalette,
      rgbChannels,
    });
    first.isAutoLevelsDisabled = isAutoLevelsDisabled;
    if (!shouldPreserveExplicitSelection || isAutoLevelsDisabled) {
      first.isAutoLevelsOn = !isAutoLevelsDisabled;
    }

    const isNoiseSuppressionDisabled =
      first.coloringMode !== String(COLOR_MODE.NEAR_IR);
    first.isNoiseSuppressionDisabled = isNoiseSuppressionDisabled;
    if (!shouldPreserveExplicitSelection || isNoiseSuppressionDisabled) {
      first.isNoiseSuppressionOn = !isNoiseSuppressionDisabled;
    }
  }

  setImageReturnsNoToggle(first);

  return {
    ...currentImageSettings,
    [selectedImagePanelSn]: sensorImageSettings,
  };
};

export const prepareSelectedSensorImageStateForObservables = ({
  metadata,
  imageDestaggerer,
  imageSettings,
  selectedImagePanelSn,
  availableImageColorModes,
  lastUserImagePaletteBySensorIdxAndMode,
  lastUserImageColorModeBySensorIdx,
  rgbChannels,
}: {
  metadata: Array<{ productSn?: string; sn?: string }>;
  imageDestaggerer: { channelFields: (COLOR_MODE | string)[] };
  imageSettings: Record<string, ImageSettings[]>;
  selectedImagePanelSn: string;
  availableImageColorModes: (COLOR_MODE | string)[];
  lastUserImagePaletteBySensorIdxAndMode: Record<
    string,
    Record<number, Record<string, ColorPaletteName>>
  >;
  lastUserImageColorModeBySensorIdx?: ImageColorModeMemory;
  rgbChannels: string[];
}):
  | {
      selectedSensorSettingsArray: ImageSettings[];
      selectedVisibleSettings: ImageSettings[];
    }
  | undefined => {
  const selectedSensorSettingsArray = imageSettings[selectedImagePanelSn];
  if (!selectedSensorSettingsArray) {
    return undefined;
  }

  normalizeSelectedSensorImageSettingsForAvailableModes({
    selectedSensorSettingsArray,
    availableImageColorModes,
    selectedImagePanelSn,
    lastUserImagePaletteBySensorIdxAndMode,
  });

  const firstVisibleSetting = selectedSensorSettingsArray[0];
  if (firstVisibleSetting && rgbChannels.length > 0) {
    const sensorSupportedRgbModes = availableImageColorModes
      .map(String)
      .filter((mode) =>
        isRgbColorMode({
          coloringMode: mode,
          rgbChannels,
        })
      );
    const sensorSupportsRgb = sensorSupportedRgbModes.length > 0;

    const { shouldPreserveExplicitSelection } = assessInitialImageSelection({
      first: firstVisibleSetting,
      orderedValidImageModes: availableImageColorModes.map(String),
      rgbChannels,
      hasExplicitColorModeSelection:
        lastUserImageColorModeBySensorIdx?.[selectedImagePanelSn]?.[0] ===
        firstVisibleSetting.coloringMode,
    });

    if (sensorSupportsRgb && !shouldPreserveExplicitSelection) {
      firstVisibleSetting.coloringMode = sensorSupportedRgbModes.includes(
        COLOR_MODE.RGB
      )
        ? COLOR_MODE.RGB
        : sensorSupportedRgbModes[0];
      setRgbPaletteOption(firstVisibleSetting, true);
      firstVisibleSetting.colorPalette = 'RGB';
      firstVisibleSetting.isAutoLevelsOn = true;
      firstVisibleSetting.isAutoLevelsDisabled = false;
      firstVisibleSetting.isNoiseSuppressionOn = false;
      firstVisibleSetting.isNoiseSuppressionDisabled = true;
    }
  }

  const selectedSensorMetadata = findSensorMetadataByIdentifier(
    metadata as SDKSensorInfoAugmented[],
    selectedImagePanelSn
  );
  if (!selectedSensorMetadata) {
    console.warn(
      `Metadata not found for selected sensor ${selectedImagePanelSn} during update.`
    );
    imageDestaggerer.channelFields = [];
    return undefined;
  }

  const selectedVisibleSettings = selectedSensorSettingsArray.filter(
    (s) => s.showImage
  );

  normalizeVisibleImageSettingsForDisplay({
    selectedVisibleSettings,
    rgbChannels,
  });

  imageDestaggerer.channelFields = Array.from(
    new Set(selectedVisibleSettings.map((s) => s.coloringMode))
  );

  normalizeSelectedSensorImageOptions({
    selectedSensorSettingsArray,
    availableImageColorModes,
  });

  return { selectedSensorSettingsArray, selectedVisibleSettings };
};

const imageColorerCache: { [key: string]: ImageColorer } = {};

export const clearImageColorerCache = (): void => {
  Object.keys(imageColorerCache).forEach((key) => {
    delete imageColorerCache[key];
  });
};

const getCachedImageColorer = ({
  coloringMode,
  colorPalette,
  whichReturns,
  isRgb,
  imageIndex,
}: {
  coloringMode: COLOR_MODE | string;
  colorPalette: string;
  whichReturns: number[];
  isRgb: boolean;
  imageIndex: number;
}): ImageColorer => {
  const key = `${coloringMode}-${colorPalette}-${whichReturns.join(
    ','
  )}-${imageIndex}`;
  if (!imageColorerCache[key]) {
    imageColorerCache[key] = new ImageColorer(
      coloringMode,
      allColorPalettes[colorPalette as keyof typeof allColorPalettes],
      isRgb
    );
  }
  return imageColorerCache[key];
};

export function createSelectedImageObservable({
  visibleImageSetting,
  selectedSensorSettingsArray,
  rgbChannels,
  imageObservable,
  destroy$,
}: {
  visibleImageSetting: ImageSettings;
  selectedSensorSettingsArray: ImageSettings[];
  rgbChannels: string[];
  imageObservable: Observable<DataMessage>;
  destroy$: Subject<void>;
}): Observable<ChannelFieldRGBAData> | typeof NEVER {
  const { coloringMode, colorPalette } = visibleImageSetting;
  const visibleReturnIndex = resolveReturnIndexFromColorMode(coloringMode);
  const isRgb = isRgbColorMode({ coloringMode, rgbChannels });
  const imageIndex = selectedSensorSettingsArray.indexOf(visibleImageSetting);
  const imageColorer = getCachedImageColorer({
    coloringMode,
    colorPalette,
    whichReturns: visibleReturnIndex > -1 ? [visibleReturnIndex] : [],
    isRgb,
    imageIndex,
  });
  const imageSuppressor = new ImageNoiseSuppressor([]);
  const imageAutoLeveler = new ImageAutoleveler([]);
  const imageScaler = new ImageScaler([]);

  if (!imageObservable) return NEVER;
  return imageObservable.pipe(
    takeUntil(destroy$),
    map((message: DataMessage) =>
      applyPerImageTransforms({
        msg: copyDataMessage(message),
        imageSettings: visibleImageSetting,
        rgbChannels,
        processors: {
          imageSuppressor,
          imageAutoLeveler,
          imageScaler,
        },
        imageIndex,
      })
    ),
    map((message: DataMessage) => imageColorer.colorImage(message)),
    catchError((err) => {
      console.error(
        `Error in image observable pipeline for mode ${coloringMode}:`,
        err
      );
      return NEVER;
    }),
    share({
      resetOnRefCountZero: false,
    })
  );
}

// Builds the next 2D image slice and wires per-panel observables in one place.
export const buildTwoDImageSettingsRuntimeState = ({
  currentState,
  partialNewSettings,
  metadata,
  imageDestaggerer,
  imageObservable,
  availableImageColorModes,
  lastUserImagePaletteBySensorIdxAndMode,
  lastUserImageColorModeBySensorIdx,
  rgbChannels,
}: {
  currentState: TwoDImageState;
  partialNewSettings: Partial<TwoDImageState>;
  metadata: Array<{ productSn?: string; sn?: string }>;
  imageDestaggerer: { channelFields: (COLOR_MODE | string)[] };
  imageObservable: Observable<DataMessage>;
  availableImageColorModes: (COLOR_MODE | string)[];
  lastUserImagePaletteBySensorIdxAndMode: Record<
    string,
    Record<number, Record<string, ColorPaletteName>>
  >;
  lastUserImageColorModeBySensorIdx?: ImageColorModeMemory;
  rgbChannels: string[];
}): TwoDImageState => {
  const nextTwoDImageState = mergeTwoDImageSettingsState({
    currentState,
    partialNewSettings,
  });

  const { imageSettings, selectedImagePanelSn } = nextTwoDImageState;

  // Re-create destroy subject so prior selected-sensor streams are torn down.
  if (nextTwoDImageState.imageObservableDestroy$) {
    nextTwoDImageState.imageObservableDestroy$.next();
    nextTwoDImageState.imageObservableDestroy$.complete();
  }

  const destroy$ = new Subject<void>();
  nextTwoDImageState.imageObservableDestroy$ = destroy$;
  nextTwoDImageState.imageObservables = [];

  if (selectedImagePanelSn && imageSettings[selectedImagePanelSn]) {
    const preparedSensorState = prepareSelectedSensorImageStateForObservables({
      metadata,
      imageDestaggerer,
      imageSettings,
      selectedImagePanelSn,
      availableImageColorModes,
      lastUserImagePaletteBySensorIdxAndMode,
      lastUserImageColorModeBySensorIdx,
      rgbChannels,
    });

    if (preparedSensorState) {
      const { selectedSensorSettingsArray, selectedVisibleSettings } =
        preparedSensorState;

      nextTwoDImageState.imageObservables = selectedVisibleSettings
        .map((visibleImageSetting) =>
          createSelectedImageObservable({
            visibleImageSetting,
            selectedSensorSettingsArray,
            rgbChannels,
            imageObservable,
            destroy$,
          })
        )
        .filter((obs) => obs !== NEVER);
    }
  } else {
    imageDestaggerer.channelFields = [];
    nextTwoDImageState.imageObservables = [];
  }

  return nextTwoDImageState;
};

export function filterDataMessagesBySensorSN(
  targetSn: string | null
): OperatorFunction<DataMessage, DataMessage> {
  return filter(
    (message: DataMessage) => !targetSn || message?.sensorInfo?.sn === targetSn
  );
}

/**
 * Calculates dynamic 2D image options based on sensor profile.
 * Returns are selected by color mode; return toggles are disabled.
 */
export const calculateDynamicImageOptions = (
  udpProfileLidar: string | undefined,
  baseOptions: Record<string, Partial<ImageSettings>>,
  paletteMap: Record<string, unknown>
): Record<COLOR_MODE | string, ImageSettings> => {
  if (!udpProfileLidar) {
    console.warn(
      'Cannot calculate dynamic image options: udpProfileLidar is missing.'
    );
    return Object.entries(baseOptions).reduce((acc, [key, value]) => {
      const mode = key;
      acc[mode] = {
        ...defaultImageSettings,
        ...value,
        coloringMode: mode,
        colorPaletteOptions: Object.keys(paletteMap) as ColorPaletteName[],
        returnsSettings: [{ visible: true, disabled: true }],
      };
      return acc;
    }, {} as Record<COLOR_MODE | string, ImageSettings>);
  }

  return Object.values(baseOptions).reduce(
    (acc, { coloringMode, ...imageOption }) => {
      if (coloringMode) {
        const isReflectivity = isReflectivityColorMode(coloringMode);

        acc[coloringMode] = {
          ...defaultImageSettings,
          ...imageOption,
          coloringMode,
          colorPaletteOptions: Object.keys(paletteMap) as ColorPaletteName[],
          colorPalette: isReflectivity
            ? 'CalRef'
            : imageOption.colorPalette ?? defaultImageSettings.colorPalette,
          returnsSettings: [{ visible: true, disabled: true }],
        };
      } else {
        console.warn('Skipping base option due to missing coloringMode.');
      }
      return acc;
    },
    {} as Record<COLOR_MODE | string, ImageSettings>
  );
};

type BuildUpdatedImageSettingInput = {
  prevImageSettings: ImageSettings;
  partialSettings: Partial<ImageSettings>;
  newColoringMode: COLOR_MODE | string;
  coloringModeChanged: boolean;
  isRgb: boolean;
  dynamicOptionsMap: Record<COLOR_MODE | string, ImageSettings>;
  rememberedPaletteForNewMode?: ColorPaletteName;
  rememberedAutoLevelsOn?: boolean;
  rememberedNoiseSuppressionOn?: boolean;
};

type BuildUpdatedImageSettingResult = {
  finalUpdatedSetting: ImageSettings;
  newColorPalette: ColorPaletteName;
  userChangedPaletteExplicitly: boolean;
  userChangedAutoLevelsExplicitly: boolean;
};

export const buildUpdatedImageSetting = ({
  prevImageSettings,
  partialSettings,
  newColoringMode,
  coloringModeChanged,
  isRgb,
  dynamicOptionsMap,
  rememberedPaletteForNewMode,
  rememberedAutoLevelsOn,
  rememberedNoiseSuppressionOn,
}: BuildUpdatedImageSettingInput): BuildUpdatedImageSettingResult => {
  const userChangedPaletteExplicitly =
    partialSettings.colorPalette !== undefined;

  let newColorPalette: ColorPaletteName;
  if (coloringModeChanged) {
    if (isRgb) {
      newColorPalette = 'RGB';
    } else if (userChangedPaletteExplicitly) {
      newColorPalette = partialSettings.colorPalette as ColorPaletteName;
    } else {
      const dynamicDefaultPalette =
        dynamicOptionsMap[newColoringMode]?.colorPalette;

      const isReflectivity = isReflectivityColorMode(newColoringMode);

      // when leaving RGB -> non-RGB, non-Reflectivity, default to mode default or Greyscale
      const fallbackNonReflectivity = dynamicDefaultPalette ?? 'Greyscale';

      newColorPalette = isReflectivity
        ? rememberedPaletteForNewMode ??
          dynamicDefaultPalette ??
          prevImageSettings.colorPalette
        : rememberedPaletteForNewMode ?? fallbackNonReflectivity;
    }
  } else {
    newColorPalette =
      partialSettings.colorPalette ?? prevImageSettings.colorPalette;
  }

  const paletteChanged = newColorPalette !== prevImageSettings.colorPalette;

  const reflectivityPaletteSwitchedOffCalRef =
    !coloringModeChanged &&
    typeof prevImageSettings.coloringMode === 'string' &&
    isReflectivityColorMode(prevImageSettings.coloringMode) &&
    paletteChanged &&
    prevImageSettings.colorPalette === 'CalRef' &&
    newColorPalette !== 'CalRef';

  const userChangedAutoLevelsExplicitly =
    partialSettings.isAutoLevelsOn !== undefined;
  const userChangedNoiseSuppressionExplicitly =
    partialSettings.isNoiseSuppressionOn !== undefined;

  const isAutoLevelsDisabled =
    (isReflectivityColorMode(newColoringMode) &&
      newColorPalette === 'CalRef') ||
    isWindowColorMode(newColoringMode);

  const defaultAutoLevelsOnForMode = !isAutoLevelsDisabled;

  let isAutoLevelsOn: boolean;
  if (isAutoLevelsDisabled) {
    isAutoLevelsOn = false;
  } else if (userChangedAutoLevelsExplicitly) {
    isAutoLevelsOn = partialSettings.isAutoLevelsOn as boolean;
  } else if (reflectivityPaletteSwitchedOffCalRef) {
    isAutoLevelsOn = rememberedAutoLevelsOn ?? true;
  } else if (rememberedAutoLevelsOn !== undefined) {
    isAutoLevelsOn = rememberedAutoLevelsOn;
  } else if (coloringModeChanged) {
    isAutoLevelsOn = defaultAutoLevelsOnForMode;
  } else {
    isAutoLevelsOn =
      prevImageSettings.isAutoLevelsOn ?? defaultAutoLevelsOnForMode;
  }

  const baseSettingsForUpdate = coloringModeChanged
    ? dynamicOptionsMap[newColoringMode] ?? defaultImageSettings
    : prevImageSettings;

  const isNoiseSuppressionDisabled =
    newColoringMode !== String(COLOR_MODE.NEAR_IR);
  let isNoiseSuppressionOn: boolean;
  if (isNoiseSuppressionDisabled) {
    isNoiseSuppressionOn = false;
  } else if (userChangedNoiseSuppressionExplicitly) {
    isNoiseSuppressionOn = partialSettings.isNoiseSuppressionOn as boolean;
  } else if (rememberedNoiseSuppressionOn !== undefined) {
    isNoiseSuppressionOn = rememberedNoiseSuppressionOn;
  } else if (coloringModeChanged) {
    isNoiseSuppressionOn = true;
  } else {
    isNoiseSuppressionOn =
      prevImageSettings.isNoiseSuppressionOn ??
      newColoringMode === String(COLOR_MODE.NEAR_IR);
  }

  const finalUpdatedSetting: ImageSettings = {
    ...baseSettingsForUpdate,
    ...partialSettings,
    coloringMode: newColoringMode,
    colorPalette: newColorPalette,
    isAutoLevelsDisabled,
    isAutoLevelsOn,
    isNoiseSuppressionOn,
    isNoiseSuppressionDisabled,
  };

  return {
    finalUpdatedSetting,
    newColorPalette,
    userChangedPaletteExplicitly,
    userChangedAutoLevelsExplicitly,
  };
};

type BuildUpdateImageStateChangesInput = {
  imageSettings: ImageSettingsRecord;
  sensorSn: string;
  index: number;
  partialSettings: Partial<ImageSettings>;
  preserveExplicitToggles?: boolean;
  rgbChannels: string[];
  udpProfileLidar?: string;
  twoDImageOptionsToAddBase: Record<string, Partial<ImageSettings>>;
  paletteMap: Record<string, unknown>;
  lastUserImagePaletteBySensorIdxAndMode: ImagePaletteMemory;
  lastUserImageAutoLevelsBySensorIdx: ImageAutoLevelsMemory;
  lastUserImageNoiseSuppressionBySensorIdx: ImageAutoLevelsMemory;
};

type BuildUpdateImageStateChangesResult = {
  newImageSettingsRecord: ImageSettingsRecord;
  nextLastUserImagePaletteBySensorIdxAndMode?: ImagePaletteMemory;
  nextLastUserImageAutoLevelsBySensorIdx?: ImageAutoLevelsMemory;
  nextLastUserImageNoiseSuppressionBySensorIdx?: ImageAutoLevelsMemory;
};

export const buildUpdateImageStateChanges = ({
  imageSettings,
  sensorSn,
  index,
  partialSettings,
  preserveExplicitToggles,
  rgbChannels,
  udpProfileLidar,
  twoDImageOptionsToAddBase,
  paletteMap,
  lastUserImagePaletteBySensorIdxAndMode,
  lastUserImageAutoLevelsBySensorIdx,
  lastUserImageNoiseSuppressionBySensorIdx,
}: BuildUpdateImageStateChangesInput):
  | BuildUpdateImageStateChangesResult
  | undefined => {
  const currentSensorSettings = imageSettings[sensorSn];
  if (
    !currentSensorSettings ||
    index < 0 ||
    index >= currentSensorSettings.length
  ) {
    return undefined;
  }

  const dynamicOptionsMap = calculateDynamicImageOptions(
    udpProfileLidar,
    twoDImageOptionsToAddBase,
    paletteMap
  );

  const prevImageSettings = currentSensorSettings[index];
  const includesModeOrPaletteChange =
    partialSettings.coloringMode !== undefined ||
    partialSettings.colorPalette !== undefined;

  const effectivePartialSettings = { ...partialSettings };
  if (includesModeOrPaletteChange && !preserveExplicitToggles) {
    const isPaletteStaleCarryOverOnModeChange =
      effectivePartialSettings.coloringMode !== undefined &&
      effectivePartialSettings.colorPalette !== undefined &&
      effectivePartialSettings.colorPalette === prevImageSettings.colorPalette;
    const isAutoLevelsStaleCarryOver =
      effectivePartialSettings.isAutoLevelsOn !== undefined &&
      effectivePartialSettings.isAutoLevelsOn ===
        prevImageSettings.isAutoLevelsOn;
    const isNoiseSuppressionStaleCarryOver =
      effectivePartialSettings.isNoiseSuppressionOn !== undefined &&
      effectivePartialSettings.isNoiseSuppressionOn ===
        prevImageSettings.isNoiseSuppressionOn;

    if (isPaletteStaleCarryOverOnModeChange) {
      delete effectivePartialSettings.colorPalette;
    }
    if (isAutoLevelsStaleCarryOver) {
      delete effectivePartialSettings.isAutoLevelsOn;
    }
    if (isNoiseSuppressionStaleCarryOver) {
      delete effectivePartialSettings.isNoiseSuppressionOn;
    }
  }

  const newColoringMode =
    effectivePartialSettings.coloringMode ?? prevImageSettings.coloringMode;
  const coloringModeChanged =
    newColoringMode !== prevImageSettings.coloringMode;

  const isRgb = isRgbColorMode({
    coloringMode: String(newColoringMode),
    rgbChannels,
  });

  const rememberedPaletteForNewMode =
    lastUserImagePaletteBySensorIdxAndMode[sensorSn]?.[index]?.[
      String(newColoringMode)
    ];
  const rememberedAutoLevelsOn =
    lastUserImageAutoLevelsBySensorIdx[sensorSn]?.[index];
  const rememberedNoiseSuppressionOn =
    lastUserImageNoiseSuppressionBySensorIdx[sensorSn]?.[index];

  const {
    finalUpdatedSetting,
    newColorPalette,
    userChangedPaletteExplicitly,
    userChangedAutoLevelsExplicitly,
  } = buildUpdatedImageSetting({
    prevImageSettings,
    partialSettings: effectivePartialSettings,
    newColoringMode,
    coloringModeChanged,
    isRgb,
    dynamicOptionsMap,
    rememberedPaletteForNewMode,
    rememberedAutoLevelsOn,
    rememberedNoiseSuppressionOn,
  });

  const isSystemForcedPalette =
    (isRgb && newColorPalette === 'RGB') ||
    (isReflectivityColorMode(newColoringMode) && newColorPalette === 'CalRef');

  let nextLastUserImagePaletteBySensorIdxAndMode:
    | ImagePaletteMemory
    | undefined;
  if (userChangedPaletteExplicitly && !isSystemForcedPalette) {
    nextLastUserImagePaletteBySensorIdxAndMode = {
      ...lastUserImagePaletteBySensorIdxAndMode,
      [sensorSn]: {
        ...(lastUserImagePaletteBySensorIdxAndMode[sensorSn] ?? {}),
        [index]: {
          ...(lastUserImagePaletteBySensorIdxAndMode[sensorSn]?.[index] ?? {}),
          [String(newColoringMode)]: newColorPalette,
        },
      },
    };
  } else if (userChangedPaletteExplicitly && isSystemForcedPalette) {
    // User explicitly reset to the default palette — clear any custom memory
    // for this mode so switching away and back restores the default
    const idxEntry = lastUserImagePaletteBySensorIdxAndMode[sensorSn]?.[index];
    if (idxEntry?.[String(newColoringMode)] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [String(newColoringMode)]: removed, ...rest } = idxEntry;
      nextLastUserImagePaletteBySensorIdxAndMode = {
        ...lastUserImagePaletteBySensorIdxAndMode,
        [sensorSn]: {
          ...(lastUserImagePaletteBySensorIdxAndMode[sensorSn] ?? {}),
          [index]: rest,
        },
      };
    }
  }

  let nextLastUserImageAutoLevelsBySensorIdx: ImageAutoLevelsMemory | undefined;
  if (userChangedAutoLevelsExplicitly) {
    nextLastUserImageAutoLevelsBySensorIdx = {
      ...lastUserImageAutoLevelsBySensorIdx,
      [sensorSn]: {
        ...(lastUserImageAutoLevelsBySensorIdx[sensorSn] ?? {}),
        [index]: effectivePartialSettings.isAutoLevelsOn as boolean,
      },
    };
  }

  let nextLastUserImageNoiseSuppressionBySensorIdx:
    | ImageAutoLevelsMemory
    | undefined;
  if (
    effectivePartialSettings.isNoiseSuppressionOn !== undefined &&
    !finalUpdatedSetting.isNoiseSuppressionDisabled
  ) {
    nextLastUserImageNoiseSuppressionBySensorIdx = {
      ...lastUserImageNoiseSuppressionBySensorIdx,
      [sensorSn]: {
        ...(lastUserImageNoiseSuppressionBySensorIdx[sensorSn] ?? {}),
        [index]: effectivePartialSettings.isNoiseSuppressionOn,
      },
    };
  }

  const updatedSensorImages = [
    ...currentSensorSettings.slice(0, index),
    finalUpdatedSetting,
    ...currentSensorSettings.slice(index + 1),
  ];

  return {
    newImageSettingsRecord: {
      ...imageSettings,
      [sensorSn]: updatedSensorImages,
    },
    nextLastUserImagePaletteBySensorIdxAndMode,
    nextLastUserImageAutoLevelsBySensorIdx,
    nextLastUserImageNoiseSuppressionBySensorIdx,
  };
};

type ImageSettingsRecord = Record<string, ImageSettings[]>;

export const buildSwappedImageSettingsRecord = ({
  imageSettings,
  sensorSn,
  indexA,
  indexB,
}: {
  imageSettings: ImageSettingsRecord;
  sensorSn: string;
  indexA: number;
  indexB: number;
}): ImageSettingsRecord | undefined => {
  if (!imageSettings[sensorSn]) {
    return undefined;
  }

  const sensorImageSettings = imageSettings[sensorSn];
  if (
    indexA < 0 ||
    indexA >= sensorImageSettings.length ||
    indexB < 0 ||
    indexB >= sensorImageSettings.length ||
    indexA === indexB
  ) {
    return undefined;
  }

  const newSensorImageSettings = [...sensorImageSettings];
  [newSensorImageSettings[indexA], newSensorImageSettings[indexB]] = [
    newSensorImageSettings[indexB],
    newSensorImageSettings[indexA],
  ];

  return {
    ...imageSettings,
    [sensorSn]: newSensorImageSettings,
  };
};

export const buildDeletedImageSettingsRecord = ({
  imageSettings,
  sensorSn,
  index,
}: {
  imageSettings: ImageSettingsRecord;
  sensorSn: string;
  index: number;
}): ImageSettingsRecord | undefined => {
  if (!imageSettings[sensorSn]) {
    return undefined;
  }

  const currentSensorSettings = imageSettings[sensorSn];
  if (index < 0 || index >= currentSensorSettings.length) {
    return undefined;
  }

  // Prevent deleting the last image panel for a sensor
  if (currentSensorSettings.length <= 1) {
    return undefined;
  }

  const newSensorSettings = currentSensorSettings.filter((_, i) => i !== index);
  return {
    ...imageSettings,
    [sensorSn]: newSensorSettings,
  };
};

export const buildAddedImageSettingsRecord = ({
  imageSettings,
  sensorSn,
  availableImageColorModes,
  rgbChannels,
  udpProfileLidar,
  twoDImageOptionsToAddBase,
  paletteMap,
}: {
  imageSettings: ImageSettingsRecord;
  sensorSn: string;
  availableImageColorModes: (COLOR_MODE | string)[];
  rgbChannels: string[];
  udpProfileLidar: string | undefined;
  twoDImageOptionsToAddBase: Record<string, Partial<ImageSettings>>;
  paletteMap: Record<string, unknown>;
}): ImageSettingsRecord | undefined => {
  if (!imageSettings[sensorSn]) {
    return undefined;
  }

  const currentSensorSettings = imageSettings[sensorSn];
  const twoDImageOptionsToAdd = calculateDynamicImageOptions(
    udpProfileLidar,
    twoDImageOptionsToAddBase,
    paletteMap
  );

  const allowedModesOrdered = (availableImageColorModes ?? []).map(String);
  const usedColorModes = new Set(
    (currentSensorSettings || []).map((s) => String(s.coloringMode))
  );

  const nextUnusedMode =
    allowedModesOrdered.find((m) => !usedColorModes.has(m)) ??
    allowedModesOrdered[0];

  if (!nextUnusedMode) {
    return undefined;
  }

  const base = twoDImageOptionsToAdd[nextUnusedMode] ?? {
    ...defaultImageSettings,
    coloringMode: nextUnusedMode,
  };

  const isRgb = isRgbColorMode({
    coloringMode: nextUnusedMode,
    rgbChannels,
  });
  const isReflectivity = isReflectivityColorMode(nextUnusedMode);

  let initialPalette = base.colorPalette;
  if (isRgb) {
    initialPalette = 'RGB';
  } else if (isReflectivity) {
    initialPalette = 'CalRef';
  }

  const imageToAdd: ImageSettings = {
    ...base,
    coloringMode: nextUnusedMode,
    colorPalette: initialPalette,
  };

  return {
    ...imageSettings,
    [sensorSn]: [...currentSensorSettings, imageToAdd],
  };
};
